import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Text } from '@/components/ui';
import { chartAxisTick, chartColor } from '@/lib/design-system/color-system';
import { fetchAllTransactions, fetchBudgets, fetchCategories } from '@/lib/finance/api';
import { formatUsd } from '@/lib/finance/format';
import { monthLabel, monthStart } from '@/lib/finance/month';
import {
  budgetReportTotals,
  buildActualDrillModel,
  buildBudgetReport,
  type BudgetReport,
  type BudgetReportRow,
  type BudgetStatus,
  type DrillScope,
} from '@/lib/finance/report';
import type { Budget, Category, Transaction } from '@/lib/finance/types';

import { ActualDrilldown, DrillNumber, drillKey } from './ActualDrilldown.web';

const STATUS_STYLE: Record<BudgetStatus, { row: string; label: string; text: string }> = {
  'in-band': { row: '', label: 'In band', text: 'text-fg-muted' },
  over: { row: 'bg-danger-muted', label: 'Over', text: 'text-danger' },
  under: { row: 'bg-accent-muted', label: 'Under', text: 'text-accent' },
};

type SortKey = 'label' | 'minUsd' | 'maxUsd' | 'actualUsd' | 'varianceUsd' | 'status';
type SortDir = 'asc' | 'desc';
type ChartSort = 'actual' | 'variance';

const STATUS_RANK: Record<BudgetStatus, number> = { over: 0, under: 1, 'in-band': 2 };

function compareRows(a: BudgetReportRow, b: BudgetReportRow, key: SortKey): number {
  if (key === 'label') return a.label.localeCompare(b.label);
  if (key === 'status') return STATUS_RANK[a.status] - STATUS_RANK[b.status];
  return a[key] - b[key];
}

/** Default direction when a column is first selected (numbers high→low, text A→Z). */
function defaultDir(key: SortKey): SortDir {
  return key === 'label' ? 'asc' : 'desc';
}

function usd(value: number): string {
  return `$${formatUsd(value)}`;
}

function BandTooltip({ active, payload }: { active?: boolean; payload?: { payload?: unknown }[] }) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0]?.payload as
    | { name: string; min: number; max: number; actual: number; variance: number }
    | undefined;
  if (!d) return null;
  return (
    <div className="rounded-md border border-border bg-surface px-3 py-2 text-xs shadow-sm">
      <div className="mb-1 font-medium text-fg">{d.name}</div>
      <div className="text-fg-muted">
        Band: {usd(d.min)} – {usd(d.max)}
      </div>
      <div className="text-fg-muted">Actual: {usd(d.actual)}</div>
      <div className="text-fg-muted">
        Variance: {d.variance === 0 ? '—' : `${d.variance > 0 ? '+' : ''}${usd(d.variance)}`}
      </div>
    </div>
  );
}

function SortHeader({
  label,
  column,
  align,
  sort,
  onSort,
}: {
  label: string;
  column: SortKey;
  align: 'left' | 'right';
  sort: { key: SortKey; dir: SortDir };
  onSort: (key: SortKey) => void;
}) {
  const active = sort.key === column;
  return (
    <th
      className={`cursor-pointer select-none border-b border-border px-3 py-2 font-medium ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${active ? 'text-fg-muted' : 'text-fg-muted hover:text-fg-muted'}`}
      onClick={() => onSort(column)}
    >
      {label}
      <span className="ml-1 text-[10px]">{active ? (sort.dir === 'asc' ? '▲' : '▼') : ''}</span>
    </th>
  );
}

export function BudgetVsActual({ month }: { month: string }) {
  const [report, setReport] = useState<BudgetReport | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'actualUsd',
    dir: 'desc',
  });
  const [chartSort, setChartSort] = useState<ChartSort>('actual');
  const [open, setOpen] = useState<DrillScope | null>(null);

  function onSort(key: SortKey) {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: defaultDir(key) },
    );
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const monthIso = monthStart(month);
        const [categoryRows, budgetRows, transactionRows] = await Promise.all([
          fetchCategories(),
          fetchBudgets(monthIso),
          fetchAllTransactions({ month: monthIso }),
        ]);
        if (cancelled) return;
        setCategories(categoryRows);
        setBudgets(budgetRows);
        setTransactions(transactionRows);
        setReport(buildBudgetReport(categoryRows, budgetRows, transactionRows));
        setOpen(null);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to build report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [month]);

  const chartData = useMemo(() => {
    const rows = [...(report?.rows ?? [])];
    rows.sort((a, b) =>
      chartSort === 'actual'
        ? b.actualUsd - a.actualUsd
        : Math.abs(b.varianceUsd) - Math.abs(a.varianceUsd),
    );
    return rows.map((row) => ({
      name: row.label,
      min: row.minUsd,
      max: row.maxUsd,
      actual: row.actualUsd,
      variance: row.varianceUsd,
      bandBase: row.minUsd,
      bandSpan: Math.max(0, row.maxUsd - row.minUsd),
    }));
  }, [report, chartSort]);

  const sortedRows = useMemo(() => {
    const rows = [...(report?.rows ?? [])];
    rows.sort((a, b) => {
      const cmp = compareRows(a, b, sort.key);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [report, sort]);

  const tableTotals = useMemo(() => (report ? budgetReportTotals(report) : null), [report]);

  const drill = useMemo(() => {
    if (open == null || !report || !tableTotals) return null;
    const cellUsd =
      open.kind === 'all-expenses'
        ? tableTotals.actualUsd
        : open.kind === 'uncategorized'
          ? report.uncategorizedUsd
          : open.kind === 'unbudgeted'
            ? report.unbudgetedUsd
            : open.kind === 'category'
              ? (report.rows.find((row) => row.categoryId === open.categoryId)?.actualUsd ?? 0)
              : 0;
    const label =
      open.kind === 'all-expenses'
        ? 'All expenses'
        : open.kind === 'uncategorized'
          ? 'Uncategorized'
          : open.kind === 'unbudgeted'
            ? 'Unbudgeted'
            : open.kind === 'other'
              ? 'Other expenses'
              : (report.rows.find((row) => row.categoryId === open.categoryId)?.label ??
                'Category');
    return {
      key: drillKey(open, month),
      title: `${label} · ${monthLabel(month)}`,
      model: buildActualDrillModel({
        categories,
        transactions,
        budgets,
        scope: open,
        month,
        windowMonths: [month],
        mode: 'actual' as const,
        cellUsd,
      }),
    };
  }, [open, report, tableTotals, categories, transactions, budgets, month]);

  const chartHeight = Math.max(240, chartData.length * 36 + 64);

  if (loading) return <ActivityIndicator />;
  if (error)
    return (
      <Text variant="body-sm" className="text-danger">
        {error}
      </Text>
    );
  if (!report || report.rows.length === 0)
    return (
      <Text variant="body" className="text-muted">
        No budgets for this month. Set budget bands in the Budgets tab.
      </Text>
    );

  return (
    <View>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr>
              <SortHeader
                label="Category"
                column="label"
                align="left"
                sort={sort}
                onSort={onSort}
              />
              <SortHeader label="Min" column="minUsd" align="right" sort={sort} onSort={onSort} />
              <SortHeader label="Max" column="maxUsd" align="right" sort={sort} onSort={onSort} />
              <SortHeader
                label="Actual"
                column="actualUsd"
                align="right"
                sort={sort}
                onSort={onSort}
              />
              <SortHeader
                label="Variance"
                column="varianceUsd"
                align="right"
                sort={sort}
                onSort={onSort}
              />
              <SortHeader label="Status" column="status" align="left" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const style = STATUS_STYLE[row.status];
              return (
                <tr key={row.categoryId} className={style.row}>
                  <td className="border-b border-border px-3 py-1.5">{row.label}</td>
                  <td className="border-b border-border px-3 py-1.5 text-right tabular-nums">
                    {formatUsd(row.minUsd)}
                  </td>
                  <td className="border-b border-border px-3 py-1.5 text-right tabular-nums">
                    {formatUsd(row.maxUsd)}
                  </td>
                  <td className="border-b border-border px-3 py-1.5 text-right tabular-nums">
                    <DrillNumber
                      text={formatUsd(row.actualUsd)}
                      selected={
                        open != null &&
                        drillKey(open, month) ===
                          drillKey({ kind: 'category', categoryId: row.categoryId }, month)
                      }
                      label={`Show transactions for ${row.label}`}
                      onPress={() =>
                        setOpen((current) =>
                          current &&
                          drillKey(current, month) ===
                            drillKey({ kind: 'category', categoryId: row.categoryId }, month)
                            ? null
                            : { kind: 'category', categoryId: row.categoryId },
                        )
                      }
                    />
                  </td>
                  <td
                    className={`border-b border-border px-3 py-1.5 text-right tabular-nums ${style.text}`}
                  >
                    {row.varianceUsd === 0
                      ? '—'
                      : `${row.varianceUsd > 0 ? '+' : ''}${formatUsd(row.varianceUsd)}${
                          row.variancePct != null
                            ? ` (${row.variancePct > 0 ? '+' : ''}${Math.round(row.variancePct * 100)}%)`
                            : ''
                        }`}
                  </td>
                  <td className={`border-b border-border px-3 py-1.5 ${style.text}`}>
                    {style.label}
                  </td>
                </tr>
              );
            })}
            {report.unbudgetedUsd > 0 ? (
              <tr className="bg-warning-muted">
                <td className="border-b border-border px-3 py-1.5 italic text-fg-muted">
                  Unbudgeted spend
                </td>
                <td className="border-b border-border px-3 py-1.5" />
                <td className="border-b border-border px-3 py-1.5" />
                <td className="border-b border-border px-3 py-1.5 text-right tabular-nums">
                  <DrillNumber
                    text={formatUsd(report.unbudgetedUsd)}
                    selected={open?.kind === 'unbudgeted'}
                    label="Show unbudgeted transactions"
                    onPress={() =>
                      setOpen((current) =>
                        current?.kind === 'unbudgeted' ? null : { kind: 'unbudgeted' },
                      )
                    }
                  />
                </td>
                <td className="border-b border-border px-3 py-1.5" />
                <td className="border-b border-border px-3 py-1.5" />
              </tr>
            ) : null}
            {report.uncategorizedUsd > 0 ? (
              <tr>
                <td className="border-b border-border px-3 py-1.5 italic text-fg-muted">
                  Uncategorized
                </td>
                <td className="border-b border-border px-3 py-1.5" />
                <td className="border-b border-border px-3 py-1.5" />
                <td className="border-b border-border px-3 py-1.5 text-right tabular-nums">
                  <DrillNumber
                    text={formatUsd(report.uncategorizedUsd)}
                    selected={open?.kind === 'uncategorized'}
                    label="Show uncategorized transactions"
                    onPress={() =>
                      setOpen((current) =>
                        current?.kind === 'uncategorized' ? null : { kind: 'uncategorized' },
                      )
                    }
                  />
                </td>
                <td className="border-b border-border px-3 py-1.5" />
                <td className="border-b border-border px-3 py-1.5" />
              </tr>
            ) : null}
          </tbody>
          {tableTotals ? (
            <tfoot>
              <tr className="bg-surface-raised">
                <td className="border-t-2 border-border-strong px-3 py-2 font-semibold">Total</td>
                <td className="border-t-2 border-border-strong px-3 py-2 text-right font-semibold tabular-nums">
                  {formatUsd(tableTotals.minUsd)}
                </td>
                <td className="border-t-2 border-border-strong px-3 py-2 text-right font-semibold tabular-nums">
                  {formatUsd(tableTotals.maxUsd)}
                </td>
                <td className="border-t-2 border-border-strong px-3 py-2 text-right font-semibold tabular-nums">
                  <DrillNumber
                    text={formatUsd(tableTotals.actualUsd)}
                    selected={open?.kind === 'all-expenses'}
                    label="Show every expense charge"
                    onPress={() =>
                      setOpen((current) =>
                        current?.kind === 'all-expenses' ? null : { kind: 'all-expenses' },
                      )
                    }
                  />
                </td>
                <td className="border-t-2 border-border-strong px-3 py-2 text-right font-semibold tabular-nums">
                  {tableTotals.varianceUsd === 0
                    ? '—'
                    : `${tableTotals.varianceUsd > 0 ? '+' : ''}${formatUsd(tableTotals.varianceUsd)}`}
                </td>
                <td className="border-t-2 border-border-strong px-3 py-2" />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {drill ? (
        <ActualDrilldown
          key={drill.key}
          title={drill.title}
          model={drill.model}
          categories={categories}
          onClose={() => setOpen(null)}
        />
      ) : null}

      <Text variant="caption" className="mt-3 text-muted">
        The total is every expense charge this month, the same figure as ledger debit. Click an
        actual to list the transactions in it. Credits and transfers are left out.
      </Text>

      <View className="mt-6">
        <View className="mb-2 flex-row items-center gap-2">
          <Text variant="h3">Actual vs band</Text>
          <View className="flex-1" />
          <Text variant="caption" className="text-muted">
            Sort by
          </Text>
          <select
            className="cs-input h-8 w-40 py-0"
            value={chartSort}
            onChange={(e) => setChartSort(e.target.value as ChartSort)}
          >
            <option value="actual">Highest actual</option>
            <option value="variance">Largest variance</option>
          </select>
        </View>
        <div style={{ width: '100%', height: chartHeight }}>
          <ResponsiveContainer>
            <BarChart
              layout="vertical"
              data={chartData}
              barCategoryGap="20%"
              margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
            >
              <CartesianGrid stroke={chartColor.grid} strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                tick={chartAxisTick}
                tickFormatter={(value: number) => `$${Math.round(value).toLocaleString('en-US')}`}
              />
              <YAxis type="category" dataKey="name" width={170} interval={0} tick={chartAxisTick} />
              <Tooltip content={<BandTooltip />} cursor={{ fill: chartColor.cursor }} />
              <Legend wrapperStyle={{ color: chartColor.tick, fontSize: 12 }} />
              <Bar
                dataKey="bandBase"
                stackId="band"
                fill="transparent"
                legendType="none"
                isAnimationActive={false}
              />
              <Bar dataKey="bandSpan" stackId="band" name="Budget band" fill={chartColor.band} />
              <Bar dataKey="actual" name="Actual" fill={chartColor.series} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </View>
    </View>
  );
}
